import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  forwardRef,
  HostListener,
  inject,
  input,
  model,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MotifFormsModule, SelectComponent } from '@ey-xd/ng-motif';
import { FilterDropdownOption } from '@models/filter.model';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-lookup-select',
  standalone: true,
  imports: [MotifFormsModule, FormsModule],
  templateUrl: './lookup-select.component.html',
  styleUrl: './lookup-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LookupSelectComponent),
      multi: true,
    },
  ],
})
export class LookupSelectComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('lookupSelect') private lookupSelect?: SelectComponent;

  public readonly options = model<FilterDropdownOption[]>([]);
  public readonly optionsSource = input.required<(search: string) => Observable<FilterDropdownOption[]>>();
  public readonly key = input.required<string>();
  public readonly labelPosition = input<string>('outside');
  public readonly placeholder = input<string | undefined>();
  public readonly multiple = input<boolean>(false);
  public readonly ariaRequired = input<boolean>(false);
  public readonly selectionChange = output<FilterDropdownOption | FilterDropdownOption[] | null>();
  // These event names are part of the component API.
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  public readonly onLoading = output<void>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  public readonly onLoaded = output<void>();

  protected readonly isLoading = signal(false);
  protected readonly noResultsMessage = computed(() => {
    if (this.isLoading()) {
      return 'Loading...';
    }
    return 'No options found';
  });

  protected selectedValue: FilterDropdownOption | FilterDropdownOption[] | null = null;
  protected isDisabled = false;
  readonly #changeDetector = inject(ChangeDetectorRef);
  #filterRequestVersion = 0;
  #activeRequestId = 0;
  #reloadOptionsOnEmptySearch = false;
  #openOptionsListTimeout?: ReturnType<typeof setTimeout>;
  #preloadedOptionsHandled = false;
  #hasRequestedOptions = false;

  protected readonly options$ = toObservable(this.options);
  protected readonly optionsSource$ = toObservable(this.optionsSource);
  protected readonly search$ = new BehaviorSubject<string>('');

  public constructor() {
    effect(() => {
      const options = this.options();
      this.refreshMotifFilter(this.search$.getValue());

      if (!this.#preloadedOptionsHandled && !this.#hasRequestedOptions && options.length > 0) {
        this.#preloadedOptionsHandled = true;
        this.onLoaded.emit();
      }
    });

    combineLatest([this.options$, this.optionsSource$, this.search$])
      .pipe(
        debounceTime(250),
        distinctUntilChanged(([, , prevSearch], [, , currSearch]) => {
          const isSameSearch = prevSearch === currSearch;
          return isSameSearch;
        }),
        filter(([options, optionsSource, search]) => {
          if (!options) {
            return false;
          }

          if (!optionsSource) {
            return false;
          }

          const preloadedOptions = options ?? [];
          const hasPreloadedOptions = (preloadedOptions.length ?? 0) > 0;
          const searchIsEmpty = search === null || search === '';
          const noSelectedValue = !this.selectedValue;

          if (hasPreloadedOptions && searchIsEmpty && noSelectedValue && !this.#reloadOptionsOnEmptySearch) {
            return false;
          }

          if (searchIsEmpty) {
            this.#reloadOptionsOnEmptySearch = false;
          }

          return true;
        }),
        tap(() => {
          this.#hasRequestedOptions = true;
          this.#activeRequestId++;
          this.isLoading.set(true);
          this.onLoading.emit();
        }),
        switchMap(([, optionsSource, search]) => {
          const requestVersion = this.#filterRequestVersion;
          const activeRequestId = this.#activeRequestId;
          let wasOpen = false;

          return optionsSource(search).pipe(
            catchError(() => of([])),
            tap(options => {
              if (requestVersion !== this.#filterRequestVersion) {
                return;
              }

              wasOpen = this.lookupSelect?.containerOpen ?? false;
              const hasSelectedValue = this.multiple()
                ? Array.isArray(this.selectedValue) && this.selectedValue.length > 0
                : !!this.selectedValue;
              const shouldPreserveCurrentOptions = options.length === 0 && !!search && !hasSelectedValue;

              options = this.ensureSelectedValueIsInOptions(options);

              this.options.update(currentOptions => {
                if (shouldPreserveCurrentOptions && currentOptions.length > 0) {
                  // this is to preserve current options collection when user' search didn't return any results
                  // otherwise, empty options list will prevent Motif' dropdown from opening...
                  return currentOptions;
                }

                // this is to preserve currently selected value in scenario when user starts searching, picks option that is preloaded
                // and then the search request returns and overrides options. In this case the reference to already selected
                // value would be lost and the select would reset. With this approach we preserve reference to already selected value
                const newValues = new Set(options.map(opt => opt.value));
                const filteredCurrent = currentOptions.filter(opt => newValues.has(opt.value));
                const currentValues = new Set(filteredCurrent.map(opt => opt.value));
                const newOptions = options.filter(opt => !currentValues.has(opt.value));

                return [...filteredCurrent, ...newOptions];
              });
            }),
            finalize(() => {
              if (activeRequestId === this.#activeRequestId) {
                this.isLoading.set(false);
                this.onLoaded.emit();
                if (wasOpen) {
                  this.openOptionsList(search, true);
                }
              }
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    if (this.selectedValue) {
      this.lookupSelect?.writeValue(this.selectedValue);
    }
  }

  onChange: (value: FilterDropdownOption | FilterDropdownOption[] | null) => void = () => undefined;
  onTouched: () => void = () => undefined;

  private openOptionsList(search: string, reopenIfClosed = false) {
    this.clearOpenOptionsListTimeout();
    this.#openOptionsListTimeout = setTimeout(() => {
      this.#openOptionsListTimeout = undefined;

      if (!this.lookupSelect?.containerOpen && !reopenIfClosed) {
        return;
      }

      this.lookupSelect!.open();
      this.lookupSelect!.filterValue = search; // Preserve the search term in the input
      const input = this.lookupSelect!.inputFilter!.nativeElement as HTMLInputElement;
      input.focus();
      input.setSelectionRange(search.length, search.length);
    }, 10);
  }

  private ensureSelectedValueIsInOptions(options: FilterDropdownOption[]) {
    if (this.selectedValue) {
      if (this.multiple()) {
        const selectedValues = this.selectedValue as FilterDropdownOption[];
        selectedValues.forEach(selected => {
          options = this.ensureValueInOptionsList(options, selected);
        });
      } else {
        const selected = this.selectedValue as FilterDropdownOption;
        options = this.ensureValueInOptionsList(options, selected);
      }
    }

    return options;
  }

  private ensureValueInOptionsList(options: FilterDropdownOption[], selected: FilterDropdownOption) {
    if (!options?.some(option => option.value === selected.value)) {
      return [...(options || []), selected];
    }

    return options;
  }

  onSelectionChange(value: FilterDropdownOption | FilterDropdownOption[] | null): void {
    this.selectedValue = value;

    this.onChange(this.selectedValue);
    this.onTouched();

    // after dropdown is closed, Motif will clear filterValue,
    // so we need to set it back to preserve search term in case user opens dropdown again
    this.lookupSelect!.filterValue = this.search$.getValue();

    this.selectionChange.emit(this.selectedValue);
  }

  markAsTouched(): void {
    this.onTouched();
    this.lookupSelect?.ngControl?.control?.markAsTouched();

    setTimeout(() => {
      if (!this.lookupSelect?.containerOpen) {
        this.resetFilter();
      }
    });
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (
      this.lookupSelect?.containerOpen &&
      (!this.isInsideSelectOverlay(event.target) || this.isSelectTrigger(event.target))
    ) {
      this.markAsTouched();
    }

    if (this.search$.getValue() && !this.isInsideSelectOverlay(event.target)) {
      this.resetFilter();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.lookupSelect?.containerOpen &&
      (!this.isInsideSelectOverlay(event.target) || this.isSelectTrigger(event.target))
    ) {
      this.markAsTouched();
    }

    if (this.search$.getValue() && !this.isInsideSelectOverlay(event.target)) {
      this.resetFilter();
    }
  }

  writeValue(value: FilterDropdownOption | FilterDropdownOption[] | null): void {
    const rawValue = value as unknown;
    if (this.multiple()) {
      this.selectedValue = Array.isArray(rawValue)
        ? (rawValue.map(option => this.#toLookupOption(option)).filter(Boolean) as FilterDropdownOption[])
        : [];
    } else {
      this.selectedValue = this.#toLookupOption(rawValue);
    }

    if (this.selectedValue) {
      const selectedOptions = this.multiple()
        ? (this.selectedValue as FilterDropdownOption[])
        : [this.selectedValue as FilterDropdownOption];
      this.options.update(options => {
        const currentValues = new Set(options.map(option => option.value));
        return [...options, ...selectedOptions.filter(option => !currentValues.has(option.value))];
      });
    }

    this.lookupSelect?.writeValue(this.selectedValue);
  }

  #toLookupOption(value: unknown): FilterDropdownOption | null {
    if (value && typeof value === 'object' && 'value' in value) {
      return value as FilterDropdownOption;
    }

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const lookupValue = String(value);
    return (
      this.options().find(option => option.value === lookupValue) ?? {
        id: null,
        displayName: lookupValue,
        value: lookupValue,
      }
    );
  }

  registerOnChange(fn: (value: FilterDropdownOption | FilterDropdownOption[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.#changeDetector.markForCheck();
  }

  searchOptions(search: string): void {
    if (!search && this.search$.getValue()) {
      this.#reloadOptionsOnEmptySearch = true;
    }

    this.#filterRequestVersion++;
    this.search$.next(search);
  }

  private resetFilter(): void {
    const search = this.search$.getValue();
    this.#filterRequestVersion++;
    this.clearOpenOptionsListTimeout();

    if (!search) {
      return;
    }

    if (this.lookupSelect) {
      this.lookupSelect.filterValue = '';
      this.lookupSelect.applyFilter();
    }
    this.#reloadOptionsOnEmptySearch = true;
    this.search$.next('');
  }

  private isInsideSelectOverlay(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }

    const selectContainer = document.getElementById(`motif-select-${this.key()}-container`);
    if (selectContainer?.contains(target)) {
      return true;
    }

    const overlayPane = target.closest('.overlay-pane-select');
    return Boolean(overlayPane?.querySelector(`#motif-select-${this.key()}-container`));
  }

  private isSelectTrigger(target: EventTarget | null): boolean {
    return target instanceof Element && Boolean(target.closest('.motif-select-trigger'));
  }

  private clearOpenOptionsListTimeout(): void {
    if (this.#openOptionsListTimeout) {
      clearTimeout(this.#openOptionsListTimeout);
      this.#openOptionsListTimeout = undefined;
    }
  }

  private refreshMotifFilter(search: string): void {
    setTimeout(() => {
      if (this.lookupSelect) {
        this.lookupSelect.filterValue = search;
        this.lookupSelect.applyFilter();
      }
    });
  }
}
